/** QuickRouter — Éditeur de schéma (l'étude), façon KiCad : feuille claire, grille de 2,54 mm.
 *
 * Molette = zoom autour du curseur ; clic milieu, clic droit ou Espace + glisser = déplacer la
 * vue. Outils : Sélection (clic, glisser un symbole, R tourne, M miroir, Suppr), Fil (W : clic
 * sur une broche ou un point, coudes automatiques horizontal puis vertical, fin sur une broche
 * ou un fil, double-clic ou Entrée ; Échap annule), Étiquette (L : sur une broche ou un fil),
 * Alimentation (P : symbole choisi dans la barre). Chaque modification recalcule la netlist
 * (syncNets) : le chevelu de la carte suit le schéma.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import { schematicDrawing, textW } from '../../shared/sch-draw.js';
import { SCH_COLORS as C } from '../../shared/render-sch-svg.js';
import {
  connectivity, syncNets, moveSymbol, addWire, addLabel, labelPin, deleteSchItems, runErc,
} from '../../shared/schematic.js';
import * as ops from '../../shared/ops.js';
import { G } from '../../shared/symbols.js';

const snap = (v) => Math.round(v / G) * G;
const r3 = (v) => Math.round(v * 1000) / 1000;
const P = (pts) => pts.map(([x, y]) => `${x},${y}`).join(' ');
const same = (a, b) => Math.abs(a[0] - b[0]) < 0.01 && Math.abs(a[1] - b[1]) < 0.01;

function segDist(p, a, b) {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const l2 = dx * dx + dy * dy;
  const t = l2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2)) : 0;
  return { d: Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy)), q: [a[0] + t * dx, a[1] + t * dy] };
}

/** Coude horizontal puis vertical de a vers b. */
function elbow(a, b) {
  if (Math.abs(a[0] - b[0]) < 0.01 || Math.abs(a[1] - b[1]) < 0.01) return [b];
  return [[b[0], a[1]], b];
}

/** Retire les doublons et les sommets alignés. */
function simplify(pts) {
  const out = [];
  for (const p of pts) {
    if (out.length && same(out[out.length - 1], p)) continue;
    out.push(p);
    while (out.length >= 3) {
      const [a, b, c] = out.slice(-3);
      if (Math.abs((b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0])) < 0.001) out.splice(out.length - 2, 1);
      else break;
    }
  }
  return out;
}

/** Boîte approximative de primitives (texte compris). */
function primsBox(prims) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  const g = (x, y) => { x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); };
  for (const p of prims) {
    if (p.pts) p.pts.forEach(([x, y]) => g(x, y));
    if (p.c) { g(p.c[0] - p.r, p.c[1] - p.r); g(p.c[0] + p.r, p.c[1] + p.r); }
    if (p.t === 'text') {
      const w = textW(p.text, p.size);
      const [x, y] = p.pos;
      if (p.rot) { g(x - p.size / 2, y - w); g(x + p.size / 2, y + w); } else { g(x - w, y - p.size / 2); g(x + w, y + p.size / 2); }
    }
  }
  return [x0, y0, x1, y1];
}

/** Texte lisible dans le repère retourné (Y vers le haut). */
function Txt({ p, color }) {
  const [x, y] = p.pos;
  return (
    <g transform={`translate(${x} ${y}) scale(1 -1)${p.rot ? ' rotate(-90)' : ''}`} style={{ pointerEvents: 'none' }}>
      <text x={0} y={0} fontSize={p.size * 1.32} fill={color} textAnchor={p.anchor} dominantBaseline="central"
        fontFamily="Barlow, Arial, sans-serif" fontWeight={p.cls === 'ref' ? 600 : 500}>{p.text}</text>
    </g>
  );
}

function Prim({ p, hi }) {
  const color = hi ? C.hi : (C[p.cls] ?? C.body);
  const w = p.cls === 'wire' ? 0.3 : p.cls === 'pin' ? 0.22 : 0.25;
  if (p.t === 'line') return <polyline points={P(p.pts)} fill="none" stroke={color} strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" />;
  if (p.t === 'poly') {
    const fill = p.fill === 'body' ? C.bodyFill : p.fill === 'label' ? C.labelFill : p.fill === 'ink' ? color : 'none';
    return <polygon points={P(p.pts)} fill={fill} stroke={color} strokeWidth={w} strokeLinejoin="round" />;
  }
  if (p.t === 'circle') return <circle cx={p.c[0]} cy={p.c[1]} r={p.r} fill={p.fill ? color : 'none'} stroke={color} strokeWidth={w} />;
  if (p.t === 'text') return <Txt p={p} color={color} />;
  return null;
}

export default function SchematicView() {
  const project = useProjectStore((s) => s.project);
  const selection = useProjectStore((s) => s.selection);
  const schTool = useProjectStore((s) => s.schTool);
  const schPower = useProjectStore((s) => s.schPower);
  const highlightNet = useProjectStore((s) => s.highlightNet);
  const fitNonce = useProjectStore((s) => s.fitNonce);
  const showErc = useProjectStore((s) => s.show.drc);
  const st = useProjectStore.getState;

  const svgRef = useRef(null);
  const [size, setSize] = useState([800, 600]);
  const [view, setView] = useState({ x: 0, y: 200, k: 4 });
  const [cursor, setCursor] = useState(null);
  const [route, setRoute] = useState(null);   // { pts }
  const [ask, setAsk] = useState(null);       // { sx, sy, value, onOk }
  const drag = useRef(null);
  const spaceDown = useRef(false);
  const askRef = useRef(null);

  const lookup = st().lookup();
  const dr = useMemo(() => schematicDrawing(project, lookup), [project]); // eslint-disable-line react-hooks/exhaustive-deps
  const conn = useMemo(() => connectivity(project, lookup), [project]); // eslint-disable-line react-hooks/exhaustive-deps
  const erc = useMemo(() => (showErc ? runErc(project, lookup) : null), [project, showErc]); // eslint-disable-line react-hooks/exhaustive-deps
  const netOfPin = useMemo(() => new Map(project.nets.flatMap((n) => n.pins.map((k) => [k, n.name]))), [project.nets]);
  const labelBoxes = useMemo(() => dr.labels.map((l) => ({ id: l.label.id, box: primsBox(l.prims) })), [dr]);

  /** Net (nom) d'un groupe de connectivité. */
  const groupNet = (g) => {
    if (!g) return null;
    for (const k of g.pins) if (netOfPin.has(k)) return netOfPin.get(k);
    return [...g.names][0] ?? null;
  };
  const hiWires = useMemo(() => {
    const s = new Set();
    if (highlightNet) for (const [id, g] of conn.wires) if (groupNet(g) === highlightNet) s.add(id);
    return s;
  }, [conn, highlightNet, netOfPin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Taille du conteneur
  useEffect(() => {
    const el = svgRef.current?.parentElement;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setSize([el.clientWidth, el.clientHeight]));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Cadrage sur le dessin
  useEffect(() => {
    const [x0, y0, x1, y1] = dr.box;
    const k = Math.max(1, Math.min(14, Math.min(size[0] / (x1 - x0 + 20), size[1] / (y1 - y0 + 20))));
    setView({ k, x: (x0 + x1) / 2 - size[0] / 2 / k, y: (y0 + y1) / 2 + size[1] / 2 / k });
  }, [fitNonce, size[0] > 0 && size[1] > 0 ? Math.round(size[0] / 200) : 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // Centre de la vue : là où le panneau Empreintes pose les nouveaux composants.
  useEffect(() => {
    st().setSchCenter([r3(snap(view.x + size[0] / 2 / view.k)), r3(snap(view.y - size[1] / 2 / view.k))]);
  }, [view, size]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (schTool !== 'wire') setRoute(null); }, [schTool]);
  // La boîte de nom prend le focus après le clic qui l'a ouverte.
  useEffect(() => { if (ask) requestAnimationFrame(() => askRef.current?.focus()); }, [ask?.sx, ask?.sy]); // eslint-disable-line react-hooks/exhaustive-deps

  const toWorld = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return [view.x + (e.clientX - r.left) / view.k, view.y - (e.clientY - r.top) / view.k];
  };
  const toScreen = ([x, y]) => [(x - view.x) * view.k, (view.y - y) * view.k];
  const tol = Math.max(0.8, 6 / view.k);

  // ── Repérage sous le curseur ──
  const pinAt = (w) => {
    let best = null;
    for (const p of dr.pins) {
      const d = Math.hypot(w[0] - p.pos[0], w[1] - p.pos[1]);
      if (d <= tol && (!best || d < best.d)) best = { p, d };
    }
    return best?.p ?? null;
  };
  const wireAt = (w) => {
    let best = null;
    for (const wire of dr.wires) {
      for (let i = 1; i < wire.pts.length; i++) {
        const r = segDist(w, wire.pts[i - 1], wire.pts[i]);
        if (r.d <= tol && (!best || r.d < best.d)) best = { wire, d: r.d, q: r.q, a: wire.pts[i - 1], b: wire.pts[i] };
      }
    }
    return best;
  };
  /** Point d'un fil sous le curseur, recalé sur la grille le long du segment. */
  const pointOnWire = (hit) => {
    const { a, b, q } = hit;
    if (Math.abs(a[1] - b[1]) < 0.01) return [r3(Math.min(Math.max(snap(q[0]), Math.min(a[0], b[0])), Math.max(a[0], b[0]))), a[1]];
    if (Math.abs(a[0] - b[0]) < 0.01) return [a[0], r3(Math.min(Math.max(snap(q[1]), Math.min(a[1], b[1])), Math.max(a[1], b[1])))];
    return q.map(r3);
  };
  const labelAt = (w) => labelBoxes.find(({ box }) => w[0] >= box[0] - 0.3 && w[0] <= box[2] + 0.3 && w[1] >= box[1] - 0.3 && w[1] <= box[3] + 0.3)?.id ?? null;
  const symAt = (w) => {
    const hits = dr.symbols.filter(({ box }) => w[0] >= box[0] && w[0] <= box[2] && w[1] >= box[1] && w[1] <= box[3]);
    hits.sort((a, b) => (a.box[2] - a.box[0]) * (a.box[3] - a.box[1]) - (b.box[2] - b.box[0]) * (b.box[3] - b.box[1]));
    return hits[0] ?? null;
  };

  // ── Modifications (toujours suivies du recalcul de la netlist) ──
  const edit = (fn) => st().mutate((p, lk) => { const out = fn(p, lk); syncNets(p, lk); return out; });
  const askName = (screen, value, onOk) => setAsk({ sx: screen[0], sy: screen[1], value, onOk });

  const finishRoute = (pts) => {
    const clean = simplify(pts);
    if (clean.length >= 2) edit((p) => addWire(p, clean));
    setRoute(null);
  };

  const rotateSel = (deg) => edit((p) => {
    for (const s of p.schematic.symbols) if (selection.includes(s.comp)) moveSymbol(p, s.comp, { rot: s.rot + deg });
  });
  const mirrorSel = () => edit((p) => {
    for (const s of p.schematic.symbols) if (selection.includes(s.comp)) moveSymbol(p, s.comp, { mirror: !s.mirror });
  });
  const deleteSel = () => {
    if (!selection.length) return;
    const ids = new Set(selection);
    const locked = project.components.filter((c) => ids.has(c.id) && c.locked).map((c) => c.ref);
    edit((p) => {
      const refs = p.components.filter((c) => ids.has(c.id) && !c.locked).map((c) => c.ref);
      if (refs.length) ops.deleteComponents(p, refs);
      deleteSchItems(p, [...ids]);
    });
    st().setSelection([]);
    if (locked.length) st().setStatus(`${locked.join(', ')} : connecteurs de la carte mère, conservés`);
  };

  // ── Clavier ──
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (ask) { if (e.type === 'keydown' && e.key === 'Escape') setAsk(null); return; }
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.code === 'Space') { spaceDown.current = e.type === 'keydown'; return; }
      if (e.type !== 'keydown' || e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (e.key === 'Escape') {
        if (route) setRoute(null);
        else if (schTool !== 'select') st().setSchTool('select');
        else { st().setSelection([]); st().setHighlightNet(null); }
      } else if (e.key === 'Enter' && route) finishRoute(route.pts);
      else if (e.key === 'Backspace' && route) { if (route.pts.length > 1) setRoute({ pts: route.pts.slice(0, -1) }); else setRoute(null); }
      else if (e.key === 'Delete' || e.key === 'Backspace') deleteSel();
      else if (k === 'r') rotateSel(e.shiftKey ? -90 : 90);
      else if (k === 'm') mirrorSel();
      else if (k === 'w') st().setSchTool('wire');
      else if (k === 'l') st().setSchTool('label');
      else if (k === 'p') st().setSchTool('power');
      else if (k === 's') st().setSchTool('select');
      else if (k === 'f' || k === 'z') st().fit();
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('keyup', onKey); };
  }); // les gestionnaires lisent l'état courant à chaque rendu

  // ── Souris ──
  const onWheel = (e) => {
    const w = toWorld(e);
    const k = Math.min(60, Math.max(0.5, view.k * (e.deltaY < 0 ? 1.18 : 1 / 1.18)));
    const r = svgRef.current.getBoundingClientRect();
    setView({ k, x: w[0] - (e.clientX - r.left) / k, y: w[1] + (e.clientY - r.top) / k });
  };

  const onDown = (e) => {
    if (ask) return;
    if (e.button === 1 || e.button === 2 || spaceDown.current) { drag.current = { kind: 'pan', sx: e.clientX, sy: e.clientY, view }; return; }
    if (e.button !== 0) return;
    const w = toWorld(e);
    const pin = pinAt(w);
    const wh = !pin && wireAt(w);

    if (schTool === 'wire') {
      const target = pin ? pin.pos : wh ? pointOnWire(wh) : [r3(snap(w[0])), r3(snap(w[1]))];
      if (!route) { setRoute({ pts: [target] }); return; }
      const last = route.pts[route.pts.length - 1];
      if (same(last, target)) { finishRoute(route.pts); return; }
      const pts = [...route.pts, ...elbow(last, target)];
      // Arrivée sur une broche ou un fil (autre que le départ) : le fil est terminé.
      if ((pin || wh) && !same(target, route.pts[0])) finishRoute(pts);
      else setRoute({ pts });
      return;
    }
    if (schTool === 'label') {
      e.preventDefault(); // le clic ne doit pas reprendre le focus à la boîte de nom
      if (pin) {
        askName(toScreen(pin.pos), netOfPin.get(pin.key) ?? '', (name) => edit((p) => labelPin(p, p.components.find((c) => c.id === pin.comp), pin.num, name)));
      } else if (wh) {
        const at = pointOnWire(wh);
        askName(toScreen(at), groupNet(conn.wires.get(wh.wire.id)) ?? '', (name) => edit((p) => addLabel(p, { name, pos: at })));
      } else st().setStatus('Étiquette : cliquez une broche ou un fil');
      return;
    }
    if (schTool === 'power') {
      const name = schPower;
      if (pin) edit((p) => labelPin(p, p.components.find((c) => c.id === pin.comp), pin.num, name));
      else edit((p) => addLabel(p, { name, pos: wh ? pointOnWire(wh) : [r3(snap(w[0])), r3(snap(w[1]))] }));
      return;
    }

    // Sélection
    const additive = e.shiftKey || e.ctrlKey;
    const lab = !pin && labelAt(w);
    const sym = !lab && !wh && symAt(w);
    const id = lab || wh?.wire.id || sym?.comp.id || null;
    if (pin) st().setHighlightNet(netOfPin.get(pin.key) ?? null);
    else if (wh) st().setHighlightNet(groupNet(conn.wires.get(wh.wire.id)));
    if (!id) { if (!additive) st().setSelection([]); if (!pin) st().setHighlightNet(null); return; }
    if (!selection.includes(id)) st().select(id, additive);
    else if (additive) { st().select(id, true); return; }
    if (sym) {
      const ids = new Set(selection.includes(id) ? selection : [id]);
      const starts = Object.fromEntries(project.schematic.symbols.filter((s) => ids.has(s.comp)).map((s) => [s.comp, s.pos]));
      drag.current = { kind: 'move', start: w, starts, began: false };
    }
  };

  const onMove = (e) => {
    const w = toWorld(e);
    setCursor(w);
    const d = drag.current;
    if (d?.kind === 'pan') { setView({ ...d.view, x: d.view.x - (e.clientX - d.sx) / view.k, y: d.view.y + (e.clientY - d.sy) / view.k }); return; }
    if (d?.kind === 'move') {
      if (!d.began) {
        if (Math.hypot(w[0] - d.start[0], w[1] - d.start[1]) * view.k < 3) return;
        if (!st().beginTransient()) { drag.current = null; return; }
        d.began = true;
      }
      const dx = snap(w[0] - d.start[0]), dy = snap(w[1] - d.start[1]);
      st().applyTransient((p, lk) => {
        for (const [comp, s] of Object.entries(d.starts)) moveSymbol(p, comp, { pos: [s[0] + dx, s[1] + dy] });
        syncNets(p, lk);
      });
      return;
    }
    const pin = pinAt(w);
    if (pin) st().setStatus(`${pin.key}${pin.name && pin.name !== pin.num ? ` (${pin.name})` : ''}${netOfPin.has(pin.key) ? ` → ${netOfPin.get(pin.key)}` : ' — non reliée'}`);
  };
  const onUp = () => {
    if (drag.current?.kind === 'move' && drag.current.began) st().commitTransient();
    drag.current = null;
  };

  // ── Rendu ──
  const k = view.k;
  const selSet = new Set(selection);
  const hiPins = new Set(highlightNet ? project.nets.find((n) => n.name === highlightNet)?.pins ?? [] : []);
  const snapped = cursor ? [snap(cursor[0]), snap(cursor[1])] : null;
  const previewEnd = route && cursor ? (pinAt(cursor)?.pos ?? snapped) : null;
  const preview = route && previewEnd ? [...route.pts, ...elbow(route.pts[route.pts.length - 1], previewEnd)] : null;
  const hoverPin = cursor && schTool !== 'select' ? pinAt(cursor) : null;

  return (
    <div className="sch-wrap">
      <svg ref={svgRef} className="schematic" width={size[0]} height={size[1]} data-view={`${view.x},${view.y},${view.k}`}
        onWheel={onWheel} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
        onDoubleClick={() => { if (route) finishRoute(route.pts); }}
        onContextMenu={(e) => e.preventDefault()}
        style={{ cursor: schTool === 'select' ? 'default' : 'crosshair', background: C.paper }}>
        <defs>
          <pattern id="sch-grid" width={G * k} height={G * k} patternUnits="userSpaceOnUse" x={(-view.x * k) % (G * k)} y={(view.y * k) % (G * k)}>
            <circle cx={0.8} cy={0.8} r={k > 3 ? 0.8 : 0.5} fill={C.grid} />
          </pattern>
        </defs>
        {k > 1.4 && <rect width="100%" height="100%" fill="url(#sch-grid)" />}
        <g transform={`matrix(${k} 0 0 ${-k} ${-view.x * k} ${view.y * k})`}>
          {dr.wires.map((w) => (
            <polyline key={w.id} points={P(w.pts)} fill="none" stroke={selSet.has(w.id) ? '#2f7de1' : hiWires.has(w.id) ? C.hi : C.wire}
              strokeWidth={selSet.has(w.id) ? 0.45 : 0.3} strokeLinecap="round" strokeLinejoin="round" />
          ))}
          {dr.symbols.map((s) => (
            <g key={s.comp.id}>
              {selSet.has(s.comp.id) && <rect x={s.box[0] - 0.6} y={s.box[1] - 0.6} width={s.box[2] - s.box[0] + 1.2} height={s.box[3] - s.box[1] + 1.2} fill="#2f7de118" stroke="#2f7de1" strokeWidth={1.2 / k} strokeDasharray={`${4 / k} ${3 / k}`} />}
              {s.prims.map((p, i) => <Prim key={i} p={p} />)}
            </g>
          ))}
          {dr.pins.filter((p) => hiPins.has(p.key)).map((p) => <circle key={`h-${p.key}-${p.pos}`} cx={p.pos[0]} cy={p.pos[1]} r={0.55} fill={C.hi} />)}
          {dr.labels.map((l) => (
            <g key={l.label.id}>
              {selSet.has(l.label.id) && (() => { const b = labelBoxes.find((x) => x.id === l.label.id)?.box; return b ? <rect x={b[0] - 0.4} y={b[1] - 0.4} width={b[2] - b[0] + 0.8} height={b[3] - b[1] + 0.8} fill="none" stroke="#2f7de1" strokeWidth={1.2 / k} /> : null; })()}
              {l.prims.map((p, i) => <Prim key={i} p={p} hi={highlightNet === l.label.name} />)}
            </g>
          ))}
          {dr.dots.map((d) => <circle key={`j-${d[0]}-${d[1]}`} cx={d[0]} cy={d[1]} r={0.5} fill={C.junction} />)}
          {dr.notes.map((t) => <Txt key={t.id} p={{ pos: t.pos, size: t.size, anchor: 'start', text: t.text }} color={C.note} />)}
          {erc && [...erc.errors, ...erc.warnings].filter((x) => x.at).map((x, i) => (
            <circle key={`e${i}`} cx={x.at[0]} cy={x.at[1]} r={Math.max(1.2, 7 / k)} fill="none" stroke={C.erc} strokeWidth={1.6 / k}><title>{x.message}</title></circle>
          ))}
          {hoverPin && <circle cx={hoverPin.pos[0]} cy={hoverPin.pos[1]} r={Math.max(0.6, 5 / k)} fill="none" stroke="#2f7de1" strokeWidth={1.5 / k} />}
          {preview && <polyline points={P(preview)} fill="none" stroke={C.wire} strokeOpacity={0.7} strokeWidth={0.3} strokeDasharray="0.8 0.5" />}
          {schTool !== 'select' && snapped && !hoverPin && <circle cx={snapped[0]} cy={snapped[1]} r={0.35} fill="#2f7de1" />}
        </g>
      </svg>
      {ask && (
        <form className="sch-ask" style={{ left: Math.min(size[0] - 230, Math.max(4, ask.sx + 10)), top: Math.max(4, ask.sy - 18) }}
          onSubmit={(e) => { e.preventDefault(); const v = ask.value.trim(); if (v) ask.onOk(v); setAsk(null); }}>
          <input ref={askRef} placeholder="Nom du net (GND, +3V3, RS485_A…)" value={ask.value}
            onChange={(e) => setAsk({ ...ask, value: e.target.value })}
            onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Escape') setAsk(null); }} />
          <button className="primary" type="submit">OK</button>
        </form>
      )}
      {cursor && <div className="sch-coords">{cursor[0].toFixed(2)} ; {cursor[1].toFixed(2)} mm</div>}
    </div>
  );
}
